import { useEffect, useRef } from 'react';
import { usePreferences } from '../state/preferences.ts';
import styles from './LedgerCanvas.module.css';

export interface LedgerCanvasProps {
  /** Optional additional class name. */
  className?: string;
  /**
   * Whether this canvas is running on the Overview page cockpit.
   * Enhances the top hero cockpit illumination and active green telemetry pulses.
   */
  isOverview?: boolean;
  /** Whether the pointer listener is attached for interactive mouse effects. */
  interactive?: boolean;
  /** Whether to bound the canvas to a parent container instead of parent main area. */
  standalone?: boolean;
}

interface LinePulse {
  axis: 'x' | 'y';
  coord: number;
  pos: number;
  speed: number;
  length: number;
  color: string;
  alpha: number;
}

interface MouseSpark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
  size: number;
}

interface PointerState {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  alpha: number;
  active: boolean;
  lastMoveTime: number;
}

const DEFAULT_PULSE_COLOR = 'rgba(6, 193, 103, '; // signature emerald
const PULSE_COLORS: readonly string[] = [
  DEFAULT_PULSE_COLOR, // emerald green dot
  DEFAULT_PULSE_COLOR, // emerald green dot
  'rgba(16, 185, 129, ', // bright emerald green dot
  'rgba(56, 189, 248, ', // luminous cyan accent
];

function isDarkTheme(): boolean {
  if (typeof document === 'undefined') return false;
  const theme = document.documentElement.dataset.theme;
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true
  );
}

function isReducedMotion(): boolean {
  if (typeof document === 'undefined') return false;
  if (document.documentElement.dataset.motion === 'reduced') return true;
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

function initPulse(pulse: LinePulse, width: number, height: number): void {
  pulse.axis = Math.random() > 0.5 ? 'x' : 'y';
  const gridStep = 64; // align precisely with 64px major ledger lines
  if (pulse.axis === 'x') {
    const maxLines = Math.max(1, Math.floor(height / gridStep));
    pulse.coord = Math.floor(Math.random() * maxLines) * gridStep;
    pulse.length = 32 + Math.random() * 48;
    pulse.speed = 0.8 + Math.random() * 1.1;
    pulse.pos = -pulse.length - Math.random() * 80;
  } else {
    const maxLines = Math.max(1, Math.floor(width / gridStep));
    pulse.coord = Math.floor(Math.random() * maxLines) * gridStep;
    pulse.length = 32 + Math.random() * 48;
    pulse.speed = 0.8 + Math.random() * 1.1;
    pulse.pos = -pulse.length - Math.random() * 80;
  }
  pulse.color =
    PULSE_COLORS[Math.floor(Math.random() * PULSE_COLORS.length)] ?? DEFAULT_PULSE_COLOR;
  pulse.alpha = 0.35 + Math.random() * 0.35;
}

export function LedgerCanvas({
  className = '',
  isOverview = false,
  interactive = true,
  standalone = false,
}: LedgerCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme, motion, animation, animationSpeed, mouseAnimation } = usePreferences();

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number | null = null;
    let width = 0;
    let height = 0;

    // Animation configuration from workspace visual settings
    const speedMultiplier = (() => {
      if (animationSpeed === 'slow') return 0.5;
      if (animationSpeed === 'fast') return 2.0;
      return 1.0;
    })();

    const isAnimated = () => {
      if (animation === 'off') return false;
      if (isReducedMotion()) return false;
      return true;
    };

    // Initialize line pulses with green dots along the ledger ruling
    const pulseCount = isOverview ? 7 : 4;
    const pulses: LinePulse[] = [];
    for (let i = 0; i < pulseCount; i++) {
      const p: LinePulse = {
        axis: 'x',
        coord: 0,
        pos: 0,
        speed: 1,
        length: 40,
        color: DEFAULT_PULSE_COLOR,
        alpha: 0.5,
      };
      pulses.push(p);
    }

    // Interactive pointer state for mouse animation
    const pointer: PointerState = {
      x: -1000,
      y: -1000,
      targetX: -1000,
      targetY: -1000,
      alpha: 0,
      active: false,
      lastMoveTime: 0,
    };

    const mouseSparks: MouseSpark[] = [];

    const resize = () => {
      const rect = container.getBoundingClientRect();
      width = Math.max(1, Math.floor(rect.width));
      height = Math.max(1, Math.floor(rect.height));

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      // Re-seed pulses within new dimensions
      for (const p of pulses) {
        initPulse(p, width, height);
        // Stagger positions initially across the canvas
        p.pos = Math.random() * (p.axis === 'x' ? width : height);
      }
    };

    resize();

    const handlePointerMove = (e: MouseEvent) => {
      if (!isAnimated() || mouseAnimation === 'off') return;
      const rect = container.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;

      if (px >= 0 && px <= width && py >= 0 && py <= height) {
        pointer.targetX = px;
        pointer.targetY = py;
        if (!pointer.active) {
          pointer.x = px;
          pointer.y = py;
        }
        pointer.active = true;
        pointer.lastMoveTime = performance.now();

        // Spawn subtle trailing sparks as cursor glides over ledger ruling
        if (mouseSparks.length < 24 && Math.random() < 0.65) {
          mouseSparks.push({
            x: px + (Math.random() - 0.5) * 6,
            y: py + (Math.random() - 0.5) * 6,
            vx: (Math.random() - 0.5) * 1.4,
            vy: (Math.random() - 0.5) * 1.4,
            life: 1.0,
            color: Math.random() > 0.3 ? DEFAULT_PULSE_COLOR : 'rgba(56, 189, 248, ',
            size: 1.5 + Math.random() * 1.2,
          });
        }
      } else {
        pointer.active = false;
      }
    };

    const handlePointerLeave = () => {
      pointer.active = false;
    };

    if (interactive) {
      window.addEventListener('mousemove', handlePointerMove, { passive: true });
      window.addEventListener('mouseleave', handlePointerLeave, { passive: true });
    }

    const renderFrame = (time: number) => {
      const dark = isDarkTheme();
      const active = isAnimated();

      ctx.clearRect(0, 0, width, height);

      // Subtle atmospheric hero cockpit glow at top-center of main stage
      const heroX = width * 0.5;
      const heroY = 0;
      const breath = !active ? 0 : Math.sin(time * 0.0008 * speedMultiplier) * 8;
      const heroRadius = (isOverview ? 480 : 360) + breath;

      const heroGrad = ctx.createRadialGradient(heroX, heroY, 0, heroX, heroY, heroRadius);
      if (dark) {
        heroGrad.addColorStop(
          0,
          isOverview ? 'rgba(56, 189, 248, 0.03)' : 'rgba(56, 189, 248, 0.015)',
        );
        heroGrad.addColorStop(
          0.5,
          isOverview ? 'rgba(14, 165, 233, 0.01)' : 'rgba(14, 165, 233, 0.005)',
        );
        heroGrad.addColorStop(1, 'rgba(21, 23, 41, 0)');
      } else {
        heroGrad.addColorStop(
          0,
          isOverview ? 'rgba(56, 189, 248, 0.02)' : 'rgba(56, 189, 248, 0.01)',
        );
        heroGrad.addColorStop(1, 'rgba(244, 245, 249, 0)');
      }
      ctx.fillStyle = heroGrad;
      ctx.fillRect(0, 0, width, height);

      // Ambient telemetry radar scan sweep (gentle horizontal energy line moving down the canvas)
      if (active) {
        const scanY = ((time * 0.04 * speedMultiplier) % (height + 240)) - 120;
        if (scanY > -60 && scanY < height + 60) {
          const scanGrad = ctx.createLinearGradient(0, scanY - 30, 0, scanY + 30);
          scanGrad.addColorStop(0, 'rgba(6, 193, 103, 0)');
          scanGrad.addColorStop(
            0.5,
            dark ? 'rgba(6, 193, 103, 0.022)' : 'rgba(6, 193, 103, 0.012)',
          );
          scanGrad.addColorStop(1, 'rgba(6, 193, 103, 0)');
          ctx.fillStyle = scanGrad;
          ctx.fillRect(0, Math.max(0, scanY - 30), width, 60);
        }
      }

      // Active green telemetry pulses along the ledger grid lines
      if (active) {
        for (const pulse of pulses) {
          pulse.pos += pulse.speed * speedMultiplier;
          const max = pulse.axis === 'x' ? width : height;
          if (pulse.pos > max + pulse.length) {
            initPulse(pulse, width, height);
            continue;
          }

          ctx.save();
          if (pulse.axis === 'x') {
            const grad = ctx.createLinearGradient(
              pulse.pos,
              pulse.coord,
              pulse.pos - pulse.length,
              pulse.coord,
            );
            grad.addColorStop(0, `${pulse.color}${pulse.alpha * 0.6})`);
            grad.addColorStop(1, `${pulse.color}0)`);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(pulse.pos - pulse.length, pulse.coord);
            ctx.lineTo(pulse.pos, pulse.coord);
            ctx.stroke();

            // Luminous particle head (the green dot)
            ctx.beginPath();
            ctx.arc(pulse.pos, pulse.coord, 2.2, 0, Math.PI * 2);
            ctx.fillStyle = `${pulse.color}${Math.min(pulse.alpha + 0.45, 1)})`;
            ctx.shadowColor = `${pulse.color}0.9)`;
            ctx.shadowBlur = 6;
            ctx.fill();
          } else {
            const grad = ctx.createLinearGradient(
              pulse.coord,
              pulse.pos,
              pulse.coord,
              pulse.pos - pulse.length,
            );
            grad.addColorStop(0, `${pulse.color}${pulse.alpha * 0.6})`);
            grad.addColorStop(1, `${pulse.color}0)`);
            ctx.strokeStyle = grad;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(pulse.coord, pulse.pos - pulse.length);
            ctx.lineTo(pulse.coord, pulse.pos);
            ctx.stroke();

            // Luminous particle head (the green dot)
            ctx.beginPath();
            ctx.arc(pulse.coord, pulse.pos, 2.2, 0, Math.PI * 2);
            ctx.fillStyle = `${pulse.color}${Math.min(pulse.alpha + 0.45, 1)})`;
            ctx.shadowColor = `${pulse.color}0.9)`;
            ctx.shadowBlur = 6;
            ctx.fill();
          }
          ctx.restore();
        }
      }

      // Interactive mouse animation and motion effects
      if (active && mouseAnimation !== 'off') {
        const lerpFactor = Math.min(1, 0.16 * speedMultiplier);
        pointer.x += (pointer.targetX - pointer.x) * lerpFactor;
        pointer.y += (pointer.targetY - pointer.y) * lerpFactor;

        const inactive = performance.now() - pointer.lastMoveTime > 3000;
        const targetAlpha = pointer.active && !inactive ? 1 : 0;
        pointer.alpha += (targetAlpha - pointer.alpha) * 0.08;

        if (pointer.alpha > 0.01) {
          ctx.save();

          // 1. Mouse Spotlight / Luminous Aura
          const spotlightRadius = isOverview ? 240 : 200;
          const spotGrad = ctx.createRadialGradient(
            pointer.x,
            pointer.y,
            0,
            pointer.x,
            pointer.y,
            spotlightRadius,
          );
          if (dark) {
            spotGrad.addColorStop(0, `rgba(6, 193, 103, ${0.07 * pointer.alpha})`);
            spotGrad.addColorStop(0.45, `rgba(56, 189, 248, ${0.03 * pointer.alpha})`);
            spotGrad.addColorStop(1, 'rgba(6, 193, 103, 0)');
          } else {
            spotGrad.addColorStop(0, `rgba(6, 193, 103, ${0.035 * pointer.alpha})`);
            spotGrad.addColorStop(0.5, `rgba(56, 189, 248, ${0.015 * pointer.alpha})`);
            spotGrad.addColorStop(1, 'rgba(6, 193, 103, 0)');
          }
          ctx.fillStyle = spotGrad;
          ctx.beginPath();
          ctx.arc(pointer.x, pointer.y, spotlightRadius, 0, Math.PI * 2);
          ctx.fill();

          // 2. Interactive grid intersection highlights near cursor
          const gridStep = 64;
          const reach = 130;
          const minGX = Math.max(0, Math.floor((pointer.x - reach) / gridStep) * gridStep);
          const maxGX = Math.min(width, Math.ceil((pointer.x + reach) / gridStep) * gridStep);
          const minGY = Math.max(0, Math.floor((pointer.y - reach) / gridStep) * gridStep);
          const maxGY = Math.min(height, Math.ceil((pointer.y + reach) / gridStep) * gridStep);

          for (let gx = minGX; gx <= maxGX; gx += gridStep) {
            for (let gy = minGY; gy <= maxGY; gy += gridStep) {
              const dist = Math.hypot(gx - pointer.x, gy - pointer.y);
              if (dist < reach) {
                const prox = (1 - dist / reach) * pointer.alpha;

                // Precision micro-crosshair at intersection
                ctx.strokeStyle = dark
                  ? `rgba(6, 193, 103, ${prox * 0.45})`
                  : `rgba(6, 193, 103, ${prox * 0.35})`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(gx - 4, gy);
                ctx.lineTo(gx + 4, gy);
                ctx.moveTo(gx, gy - 4);
                ctx.lineTo(gx, gy + 4);
                ctx.stroke();

                // Luminous intersection dot
                ctx.beginPath();
                ctx.arc(gx, gy, 1.4, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(56, 189, 248, ${prox * 0.75})`;
                ctx.shadowColor = 'rgba(6, 193, 103, 0.8)';
                ctx.shadowBlur = 4;
                ctx.fill();
              }
            }
          }

          ctx.restore();
        }

        // 3. Motion trail sparks following cursor
        for (let i = mouseSparks.length - 1; i >= 0; i--) {
          const spark = mouseSparks[i];
          if (!spark) continue;
          spark.x += spark.vx * speedMultiplier;
          spark.y += spark.vy * speedMultiplier;
          spark.life -= 0.024 * speedMultiplier;
          if (spark.life <= 0) {
            mouseSparks.splice(i, 1);
            continue;
          }

          ctx.save();
          ctx.beginPath();
          ctx.arc(spark.x, spark.y, spark.size * spark.life, 0, Math.PI * 2);
          ctx.fillStyle = `${spark.color}${spark.life * 0.7})`;
          ctx.shadowColor = `${spark.color}0.85)`;
          ctx.shadowBlur = 4;
          ctx.fill();
          ctx.restore();
        }
      }

      if (active) {
        animId = requestAnimationFrame(renderFrame);
      }
    };

    // Render frame
    animId = requestAnimationFrame(renderFrame);

    // Pause on backgrounded tabs to conserve battery / CPU
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (animId !== null) {
          cancelAnimationFrame(animId);
          animId = null;
        }
      } else {
        if (animId === null && isAnimated()) {
          animId = requestAnimationFrame(renderFrame);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const observer = new ResizeObserver(() => {
      resize();
      if (!isAnimated()) {
        renderFrame(0);
      }
    });
    observer.observe(container);

    return () => {
      if (animId !== null) cancelAnimationFrame(animId);
      if (interactive) {
        window.removeEventListener('mousemove', handlePointerMove);
        window.removeEventListener('mouseleave', handlePointerLeave);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();
    };
  }, [isOverview, interactive, theme, motion, animation, animationSpeed, mouseAnimation]);

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${isOverview ? styles.overview : ''} ${
        standalone ? styles.standalone : ''
      } ${className}`.trim()}
      aria-hidden="true"
      role="presentation"
      data-testid="ledger-canvas"
      data-overview={isOverview ? 'true' : undefined}
    >
      <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
    </div>
  );
}
